import type { Variants } from 'framer-motion';

// One motion vocabulary for the Rooms surfaces. framer-motion v4 has no `whileInView`,
// so entrances are mount-based. Reduced motion is handled by callers (useReducedMotion),
// not by each variant.

/** Cubic-bezier as [x1,y1,x2,y2]: a quick out, a soft settle. */
export const EASE: [number, number, number, number] = [0.2, 0.7, 0.3, 1];

export const DURATION = { fast: 0.22, base: 0.42, slow: 0.7 } as const;

/** Parent of a staggered group: children rise in sequence. */
export const staggerParent = (stagger = 0.07, delay = 0): Variants => ({
  hidden: {},
  show: { transition: { staggerChildren: stagger, delayChildren: delay } }
});

/** A group child, or a standalone section that should rise on mount. */
export const riseItem: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: DURATION.base, ease: EASE } }
};

/** Same rise plus a touch of scale, for cards that should feel like objects. */
export const riseCard: Variants = {
  hidden: { opacity: 0, y: 18, scale: 0.985 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: DURATION.base, ease: EASE } }
};

/** Crossfade for swapped panes (console tabs, list ↔ empty state). */
export const fadeSwap: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: DURATION.base, ease: EASE } },
  exit: { opacity: 0, y: -8, transition: { duration: DURATION.fast, ease: EASE } }
};

/** Spring lift on hover, small press on tap. */
export const cardHover = {
  whileHover: { y: -3, transition: { type: 'spring', stiffness: 320, damping: 22 } },
  whileTap: { scale: 0.995 }
} as const;

/** A meter filling from empty to its share of the pool. */
export const meterFill = (pct: number) => ({
  initial: { scaleX: 0 },
  animate: { scaleX: pct / 100 },
  transition: { duration: DURATION.slow, ease: EASE }
});
